import React, { memo } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Typography,
  IconButton,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getPlatformStyles } from '../../../utils/getPlatformStyles';
import { getPlatformLogo } from '../../../utils/getPlatformLogo';
import { cardFoot, footSecondary } from '../../../utils/surfaces';

interface Game {
  id: string;
  title: string;
  year: number;
  image: string;
  platform: string;
  edition: string;
  editionName?: string;
  steelbook: boolean;
  sealed: boolean;
}

interface GameCardProps {
  game: Game;
  onMenuClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    game: Game
  ) => void;
}

function GameCard({ game, onMenuClick }: GameCardProps) {
  const platform = getPlatformStyles(game.platform);
  const Logo = getPlatformLogo(game.platform);

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.25,
          minHeight: 36,
          bgcolor: platform.bg,
          color: platform.color,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {Logo ? (
            <Logo
              style={{
                height: '36px',
                width: 'auto',
                display: 'block',
                color: game.platform === 'PS5' ? '#000000' : '#ffffff',
                flexShrink: 0,
                transform:
                  game.platform === 'PS1'
                    ? 'scale(0.85) translateY(1px)'
                    : 'translateY(1px)',
                transformOrigin: 'left center',
              }}
            />
          ) : (
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: platform.color,
                lineHeight: 1.1,
              }}
            >
              {platform.label}
            </Typography>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={(e) => onMenuClick(e, game)}
          sx={{
            color: platform.color,
            p: 0.25,
            ml: 1,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <CardMedia
        component="img"
        src={game.image}
        alt={game.title}
        sx={{
          width: '100%',
          aspectRatio: '16 / 9',
          objectFit: 'cover',
        }}
      />

      <Box
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          minHeight: 96,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.4,
          ...cardFoot(theme),
        })}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {game.title}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: footSecondary,
            fontSize: '0.75rem',
          }}
        >
          {game.edition === 'Special'
            ? game.editionName?.trim() || 'Special Edition'
            : 'Standard'}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: footSecondary }}
          >
            {game.year}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: footSecondary }}
          >
            {[game.sealed ? 'Sealed' : null, game.steelbook ? 'Steelbook' : null]
              .filter(Boolean)
              .join(' • ')}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

export default memo(GameCard);